import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import * as AuthModule from 'modules/auth';
import { PublicTestsCatalog } from './PublicTestsCatalog';
import { PortalHeading } from './PortalHeading';
import { PortalCodeEntry } from './PortalCodeEntry';
import { TodayTestCard } from './TodayTestCard';
import { SetGoalCard } from './SetGoalCard';
import { IS_IO } from 'lib/site';
import { LeaderboardCard } from './LeaderboardCard';
import { InviteFriendCard } from 'components/portal/InviteFriendCard';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { WelcomeBonusDialog } from 'components/portal/WelcomeBonusDialog';
import { useWelcomeBonus } from 'modules/portal-plans/useWelcomeBonus';

function greetingKey(hour: number): string {
  if (hour < 12) return 'portal.greeting_morning';
  if (hour < 18) return 'portal.greeting_afternoon';
  return 'portal.greeting_evening';
}

/** Learner home: today's cards, then the public test marketplace. */
export const PortalHome: React.FC = () => {
  const { t } = useTranslation();
  const { state } = AuthModule.useAuthContext();

  const [searchParams, setSearchParams] = useSearchParams();
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'START' | 'PRO' | 'PREMIUM'>('PRO');
  const [isOnboardingOffer, setIsOnboardingOffer] = useState(false);
  const [showWelcomeBonus, setShowWelcomeBonus] = useState(false);
  // No live bonus (switched off in admin, 0, or ended) means no bonus dialog at all.
  const welcomeBonus = useWelcomeBonus();

  useEffect(() => {
    const hasOfferParam = searchParams.get('suggestPlan') === 'true';
    const hasSubscribeParam = searchParams.get('subscribe') === 'true';
    const planParam = searchParams.get('plan');
    const hasWelcomeParam = searchParams.get('welcomeBonus') === 'true';
    let hasSessionFlag = false;
    let hasWelcomeSession = false;
    try {
      hasSessionFlag = sessionStorage.getItem('suggest_plan_onboarding') === 'true';
      hasWelcomeSession = sessionStorage.getItem('onboarding_welcome_bonus') === 'true';
    } catch {
      // ignore
    }

    if (hasWelcomeParam || hasWelcomeSession) {
      setShowWelcomeBonus(true);
      try {
        sessionStorage.removeItem('onboarding_welcome_bonus');
      } catch {
        // ignore
      }
      if (hasWelcomeParam) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('welcomeBonus');
        setSearchParams(nextParams, { replace: true });
      }
    }

    if (hasOfferParam || hasSubscribeParam || planParam || hasSessionFlag) {
      if (planParam === 'START' || planParam === 'PRO' || planParam === 'PREMIUM') {
        setSelectedPlan(planParam);
        setIsOnboardingOffer(false);
      } else if (hasSubscribeParam) {
        setSelectedPlan('PRO');
        setIsOnboardingOffer(false);
      } else {
        setSelectedPlan('PRO');
        setIsOnboardingOffer(true);
      }
      setShowPlanModal(true);
      try {
        sessionStorage.removeItem('suggest_plan_onboarding');
      } catch {
        // ignore
      }
      if (hasOfferParam || hasSubscribeParam || planParam) {
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('suggestPlan');
        nextParams.delete('subscribe');
        nextParams.delete('plan');
        setSearchParams(nextParams, { replace: true });
      }
    }
  }, [searchParams, setSearchParams]);

  const firstName = (state.user?.name || '').trim().split(/\s+/)[0] || state.user?.name || '';
  const userBalance = state.user?.balanceSom ?? 0;

  return (
    <div className="flex flex-col">
      <PortalHeading
        title={t(greetingKey(new Date().getHours()), { name: firstName })}
        description={t('portal.public_subline')}
        aside={<PortalCodeEntry className="hidden md:flex" />}
      />

      {/* One test a day, chosen from the exam picked at onboarding. Renders nothing without a learner profile. */}
      <div className="mt-5 flex flex-col gap-4 empty:hidden md:mt-6">
        <TodayTestCard />
        <SetGoalCard />
        {!IS_IO && <LeaderboardCard />}
        <InviteFriendCard compact />
      </div>

      <div id="public-catalog-section" className="mt-5 md:mt-6">
        <PublicTestsCatalog userBalance={userBalance} />
      </div>

      <PlanCheckoutModal
        open={showPlanModal}
        onOpenChange={setShowPlanModal}
        defaultPlan={selectedPlan}
        onboardingOffer={isOnboardingOffer}
      />

      <WelcomeBonusDialog
        open={showWelcomeBonus && welcomeBonus !== null}
        onOpenChange={setShowWelcomeBonus}
        balanceSom={userBalance > 0 ? userBalance : welcomeBonus?.amountSom}
        userName={firstName}
        onStartTests={() => {
          setShowWelcomeBonus(false);
          setTimeout(() => {
            const el = document.getElementById('public-catalog-section');
            el?.scrollIntoView({ behavior: 'smooth' });
          }, 100);
        }}
      />
    </div>
  );
};

export default PortalHome;
